const API_BASE = '/api';

export type OAuthProvider = 'microsoft' | 'google' | 'apple' | 'facebook';
export type AuthProviderKind = OAuthProvider | 'password';

/**
 * In-memory auth state for OAuth (Bearer) sessions. Password sessions use the
 * HttpOnly `session` cookie and need none of this. OAuth tokens are not
 * persisted across reloads (the provider can re-issue silently).
 */
let authProvider: AuthProviderKind | null = null;
let accessToken: string | null = null;
let authEmail: string | null = null;
let authName: string | null = null;

export function setAuthSession(opts: {
  provider: AuthProviderKind;
  token?: string | null;
  email?: string | null;
  name?: string | null;
}): void {
  authProvider = opts.provider;
  accessToken = opts.token ?? null;
  authEmail = opts.email ?? null;
  authName = opts.name ?? null;
}

export function clearAuthSession(): void {
  authProvider = null;
  accessToken = null;
  authEmail = null;
  authName = null;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * Auth headers for the current session. OAuth (Bearer) sessions carry the token
 * + provider hints; password sessions rely on the `session` cookie and get none.
 * Exported so non-`request` callers (e.g. the SSE `fetch` in the chat module)
 * can authenticate consistently.
 */
export function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  if (accessToken && authProvider && authProvider !== 'password') {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['X-Auth-Provider'] = authProvider;
    if (authEmail) headers['X-Auth-Email'] = authEmail;
    if (authName) headers['X-Auth-Name'] = authName;
  }
  return headers;
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const hasBody = options?.body !== undefined && options?.body !== null;
  const headers: Record<string, string> = {
    ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
    // OAuth Bearer session hints; password sessions rely on the cookie.
    ...getAuthHeaders(),
    ...((options?.headers as Record<string, string>) || {}),
  };

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new ApiError(
      (body as { message?: string }).message || `Request failed: ${response.status}`,
      response.status,
    );
  }

  // 204 No Content (and other empty bodies) have nothing to parse.
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

export const apiClient = {
  get: <T>(url: string, params?: Record<string, string>) => {
    const qs = params ? buildQueryString(params) : '';
    return request<T>(`${url}${qs}`);
  },
  post: <T>(url: string, data?: unknown) =>
    request<T>(url, { method: 'POST', ...(data !== undefined && { body: JSON.stringify(data) }) }),
  put: <T>(url: string, data?: unknown) =>
    request<T>(url, { method: 'PUT', ...(data !== undefined && { body: JSON.stringify(data) }) }),
  patch: <T>(url: string, data?: unknown) =>
    request<T>(url, { method: 'PATCH', ...(data !== undefined && { body: JSON.stringify(data) }) }),
  delete: <T>(url: string) => request<T>(url, { method: 'DELETE' }),
};

export function buildQueryString(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter(
    (entry): entry is [string, string] => entry[1] !== undefined && entry[1] !== '',
  );
  if (entries.length === 0) return '';
  return '?' + new URLSearchParams(entries).toString();
}

export function buildEndpoint(
  path: string,
  params?: Record<string, string | undefined>,
): string {
  return params ? `${path}${buildQueryString(params)}` : path;
}

export function extractErrorMessage(error: unknown, fallback = 'An error occurred'): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message;
  }
  return fallback;
}
